// ─────────────────────────────────────────────────────────────
// SortBeat Daily — Chain Detection Algorithm
// Pure function. No side effects. Fully unit-testable.
// ─────────────────────────────────────────────────────────────
import { Chain, Grid, LANE_COUNT, ROW_COUNT, MIN_CHAIN_LENGTH, GridCell } from '../types/game';

/**
 * Scan every lane for vertical runs of MIN_CHAIN_LENGTH+ same-colour cells.
 * Returns all detected chains. A cell can only belong to one chain.
 *
 * @param grid - The current 6×12 grid [laneIndex][row], row 0 = bottom.
 * @returns Array of detected chains, ordered lane-first then row-ascending.
 */
export function detectChains(grid: Grid): Chain[] {
  const chains: Chain[] = [];
  const claimed = Array.from({ length: LANE_COUNT }, () =>
    new Array<boolean>(ROW_COUNT).fill(false)
  );

  for (let li = 0; li < LANE_COUNT; li++) {
    let row = 0;
    while (row < ROW_COUNT) {
      const color = grid[li][row];
      if (color === null || claimed[li][row]) {
        row++;
        continue;
      }

      // Extend the run as far as possible
      let runEnd = row + 1;
      while (runEnd < ROW_COUNT && grid[li][runEnd] === color && !claimed[li][runEnd]) {
        runEnd++;
      }

      const runLength = runEnd - row;
      if (runLength >= MIN_CHAIN_LENGTH) {
        const itemIds: string[] = [];
        for (let r = row; r < runEnd; r++) {
          claimed[li][r] = true;
          // Cell id convention: "li_r" — matches Item.id format set in store
          itemIds.push(`${li}_${r}`);
        }
        chains.push({
          laneIndex: li,
          startRow: row,
          length: runLength,
          color,
          itemIds,
        });
        row = runEnd;
      } else {
        row++;
      }
    }
  }

  return chains;
}

/**
 * Returns true if the given grid has at least one detectable chain.
 * Use for quick "would this move create a chain?" look-ahead.
 */
export function hasAnyChain(grid: Grid): boolean {
  return detectChains(grid).length > 0;
}
